import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import useErrorStore from '@common/common/stores/error';
import { sendAnomalyReport } from '../utils/anomalyReporter';
import type { ErrorStateObj } from '@common/common/types/error';
import {
    getIsReportVisibleApiUser,
    getTimeseriesDataApiUser,
    getModuleDataApiUser,
    // getReportCompletionGageApiUser,
    getSubscriptionStatusApiUser,
    getFeedbackQuestionApiUser,
    postFeedbackAnswerApiUser,
    postSubjectiveFeedbackApiUser,
    getReportExistingEndDatesApiUser,
    getEligibilityTrackingCardDataApiUser,
    postClickReportPublishButtonApiUser,
    getTrendReportShareTokenApiUser,
    getTrendReportTimeseriesForShareApiUser,
    getTrendReportModuleDataForShareApiUser,
} from '@common/trend-report-common';
import type {
    Evaluation,
    // ReportCompletionGageResDto,
    ReportPublishingTrackingData,
    TimeSeriesResDto,
    TrendReportResDto,
    UserFeedback,
} from '@common/openapi-client/openapi';
import type { RequestFeedbackData } from '@common/trend-report-common';
import type { MonthlyTimeseriesData } from '@common/trend-report-common';

interface PrepareReportData {
    userName: string;
}

const useProReportStore = defineStore('proReport', () => {
    const errorStore = useErrorStore();

    const timeseriesData = ref<TimeSeriesResDto | null>(null);
    const moduleData = ref<TrendReportResDto | null>(null);
    const currentSelectedEvaluation = ref<Evaluation | null>(null);
    const moduleDataMap = ref<Record<string, TrendReportResDto>>({});
    const eligibilityTrackingCardData = ref<ReportPublishingTrackingData | null>(null);

    const prepareReportData = ref<PrepareReportData | null>(null);
    // const currentModuleEndDate = ref<string | null>(null); // answer 조회용

    // AIDEV-NOTE: 관리자 스토어(proReportAdmin.ts)의 같은 이름 맵에는 엔트리 신원 검증
    // (CachedByOwner)과 요청 세대 토큰(requestSeq)이 붙어 있는데, 여기에는 의도적으로 넣지 않았다.
    // 관리자는 한 화면에서 여러 유저의 리포트를 오가므로 endDate 단독 키가 서로 충돌하지만,
    // 유저 화면은 아래 세 조건 덕에 "클릭한 것과 다른 내용"이 생길 수 없다.
    //   1) uid 가 하나
    //   2) 키가 항상 dd='01' 로 통일 (handleChangeMonth / updateMonthlySurroundingData 양쪽)
    //   3) useOnlyPublished 같은 조회 변형이 없어 한 키에 한 종류 데이터만 들어감
    // 셋 중 하나라도 깨지면 관리자와 같은 하드닝이 필요해진다.
    //
    // 알려진 잔여 이슈 2건 (둘 다 내용 불일치는 아님):
    //   - 이전/다음 연타 시 늦게 끝난 체인이 currentSelectedEndDate 를 덮어써 다른 달에 착지할 수
    //     있다. 그 달의 데이터는 그 달 것이라 내용·라벨은 일치하고 다음 클릭에 자기교정된다.
    //   - reportType(Month/Week)은 키에 반영되지 않는다. 같은 endDate 키에 주간/월간이 섞이면
    //     충돌할 수 있다. 현재 화면 흐름에서는 재현되지 않지만 Week 를 확장할 때 주의.
    const monthlyTimeseriesMap = ref<Record<string, TimeSeriesResDto | null>>({});
    const monthlyModuleMap = ref<Record<string, TrendReportResDto | null>>({});
    const reportExistingEndDates = ref<string[]>([]);
    const monthlyTimeseriesList = ref<MonthlyTimeseriesData[]>([]);
    const currentSelectedEndDate = ref<string | null>(null);

    // 테스트용 날짜 관련
    const testDate = ref<string | undefined>(undefined);

    // 공유 리포트용 단일 데이터
    const shareTimeseriesData = ref<TimeSeriesResDto | null>(null);
    const shareModuleData = ref<TrendReportResDto | null>(null);

    const computedTimeseriesData = computed(() =>
        currentSelectedEndDate.value ? (monthlyTimeseriesMap.value[currentSelectedEndDate.value] ?? null) : null,
    );

    const computedModuleData = computed(() =>
        currentSelectedEndDate.value ? (monthlyModuleMap.value[currentSelectedEndDate.value] ?? null) : null,
    );

    // 공유 리포트용 computed 속성
    const computedShareTimeseriesData = computed(() => shareTimeseriesData.value);
    const computedShareModuleData = computed(() => shareModuleData.value);

    const getIsReportVisible = async () => {
        try {
            const resp = await getIsReportVisibleApiUser();
            if (resp.IsSuccess) return resp.Data;
        } catch (error) {
            console.error('Error fetching is report visible:', error);
        }
    };

    // ------------------------------------------------------------------
    // 서버 응답 이상 신고 (기록 전용 - 화면 동작은 바꾸지 않는다)
    // ------------------------------------------------------------------
    // AIDEV-NOTE: 세션당 (종류 x scope) 1건으로 중복을 막는다. scope 는 월 단위 이상이면
    // 'YYYY-MM', 진입 시 1회성이면 'init'.
    //
    // console.error 가 아니라 console.warn 을 쓰는 이유: webview-inapp 의 Sentry 설정에
    // captureConsoleIntegration({ levels: ['error'] }) 이 있어서, console.error 를 쓰면
    // 신고와 별개로 이벤트가 하나 더 올라가 같은 사건이 2건으로 쪼개진다.
    // (참고: 프로덕션 빌드는 terser drop_console 로 console.* 이 제거되므로, 실제 전송은
    //  아래 sendAnomalyReport 가 전담한다.)
    const reportedAnomalies = new Set<string>();

    const toMonthKey = (value: string) => value.split('T')[0].slice(0, 7); // YYYY-MM

    const isPublishedMonth = (endDate: string) =>
        reportExistingEndDates.value.some(d => toMonthKey(d) === toMonthKey(endDate));

    // AIDEV-NOTE: 방금 발급 버튼을 눌러 서버에 생긴 달.
    // report-existing-end-dates 는 발급 전에 이미 받아온 목록이라 이번 달이 빠져 있고,
    // 목록에 채워지는 건 재조회가 끝난 뒤다(MonthlyProReport 의 push). 그래서 발급 직후에는
    // isPublishedMonth 로 알 수 없다. reportExistingEndDates 를 직접 건드리지 않는 이유는
    // 그 배열이 at(0)/at(-1) 로 최신·최古 판정에 쓰여서 중복이 들어가면 곤란하기 때문이다.
    const justPublishedMonths = new Set<string>();

    // "리포트가 있어야 마땅한 달"인지. 재호출과 신고를 여기에만 건다.
    const shouldExpectReport = (endDate: string) =>
        isPublishedMonth(endDate) || justPublishedMonths.has(toMonthKey(endDate));

    // AIDEV-NOTE: 서버 상태가 바뀌어 다시 받아와야 하는 달 표시.
    //
    // 맵에서 지우지(delete) 않는 이유가 핵심이다. computedTimeseriesData/computedModuleData 가
    // 맵을 직접 읽는 computed 라서, 지우는 순간 화면이 즉시 빈다. 그 상태에서 재조회가
    // 실패하면(getTimeseriesData 는 실패 시 맵에 아무것도 쓰지 않는다) 기댈 값이 없어져
    // 백지가 그대로 굳는다. 값은 남기고 재조회만 강제하면, 새 데이터가 도착할 때까지
    // 이전 내용이 보이고 실패해도 이전 내용이 남는다.
    //
    // timeseries 와 module 을 따로 두는 이유: updateMonthlySurroundingData 는 timeseries 를
    // 모두 받은 뒤 module 을 받는다. 플래그가 하나면 timeseries 갱신이 끝나는 순간 해제돼
    // 뒤이은 module 조회가 낡은 캐시를 그대로 집어간다.
    const staleTimeseriesMonths = new Set<string>();
    const staleModuleMonths = new Set<string>();

    const markMonthStale = (generationDate: string) => {
        const month = toMonthKey(generationDate);
        staleTimeseriesMonths.add(month);
        staleModuleMonths.add(month);
    };

    const getErrorStatus = (error: unknown) => {
        const e = error as { status?: number; response?: { status?: number } };
        return e?.status ?? e?.response?.status;
    };

    const reportServerAnomaly = (kind: string, scope: string, extra: Record<string, unknown> = {}) => {
        const dedupKey = `${kind}|${scope}`;
        if (reportedAnomalies.has(dedupKey)) return;
        reportedAnomalies.add(dedupKey);

        const message = `[trend-report] 서버 응답 이상: ${kind}`;
        console.warn(message, { scope, ...extra });

        // 리포터가 주입되지 않은 앱(admin, share-report)에서는 위 console.warn 까지만 남는다.
        sendAnomalyReport(message, { kind, scope, extra });
    };

    // AIDEV-NOTE: 서버가 200 을 주면서 본문(Evaluation / ModuleBodyResDtoList)만 비워 보내는
    // 경우가 있어, 그때만 딱 1회 재호출한다.
    //
    // 무한 호출 방지 장치 (셋 다 있어야 성립한다):
    //   1) 재귀도 루프도 아닌 직선 코드다. 재호출문이 소스에 정확히 한 번만 있으므로
    //      한 번의 getXxx 호출에서 API 호출은 구조상 2회를 넘을 수 없다.
    //   2) 재호출 결과가 여전히 비어 있어도 그대로 맵에 저장한다. 비었다고 저장을 건너뛰면
    //      위쪽 캐시 가드(if (map[key]))가 계속 뚫려 월 이동/재진입마다 다시 2회씩 나간다.
    //   3) 예외는 재호출 대상이 아니다. 기존과 동일하게 즉시 중단한다.
    const EMPTY_PAYLOAD_RETRY_DELAY_MS = 300;

    const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

    // 리포트 조회 가능한 endDate 조회
    const getReportExistingEndDates = async (now: string | undefined) => {
        try {
            // now가 undefined이면 undefined로 전달
            const nowParam = now;
            const resp = await getReportExistingEndDatesApiUser(nowParam);
            let reportExistingEndDatesArr = resp.Data.reverse();
            reportExistingEndDates.value = reportExistingEndDatesArr;

            return reportExistingEndDatesArr;
        } catch (error) {
            console.error('Error fetching report existing end dates:', error);
            reportServerAnomaly('existing_end_dates_fetch_failed', 'init', {
                httpStatus: getErrorStatus(error) ?? null,
            });
        }
    };

    const getTimeseriesData = async (endDate: string, reportType = 'Month', now?: string) => {
        const month = toMonthKey(endDate);
        const cached = monthlyTimeseriesMap.value[endDate];

        // 낡음 표시가 붙은 달만 캐시를 통과시킨다.
        if (cached && !staleTimeseriesMonths.has(month)) {
            return cached;
        }
        try {
            // now 파라미터가 있으면 사용, 없으면 testDate.value 사용 (undefined이면 undefined로 처리)
            const nowParam = now || testDate.value;
            let resp = await getTimeseriesDataApiUser(endDate, nowParam, reportType);

            // Data.Evaluation 이 비어 있을 때만 1회 재호출 (여기가 유일한 재호출 지점).
            // 단 "리포트가 있어야 마땅한 달"로 한정한다. 미발행 월은 Evaluation 이 비어 있는
            // 게 정상이라, 게이트가 없으면 그런 달마다 요청이 2배가 되고 Sentry 이벤트도
            // 한 건씩 쌓인다(리포트가 하나도 없는 유저가 가장 크게 손해본다).
            if (!resp.Data?.Evaluation && shouldExpectReport(endDate)) {
                await delay(EMPTY_PAYLOAD_RETRY_DELAY_MS);
                resp = await getTimeseriesDataApiUser(endDate, nowParam, reportType);

                if (!resp.Data?.Evaluation) {
                    reportServerAnomaly('timeseries_evaluation_empty', toMonthKey(endDate), {
                        endDate,
                        reportType,
                        hasTimeSeriesData: !!resp.Data?.TimeSeriesData,
                        isPublishedMonth: isPublishedMonth(endDate),
                    });
                }
            }

            timeseriesData.value = resp.Data;
            monthlyTimeseriesMap.value[endDate] = resp.Data;
            // 새 값으로 갱신됐으니 표시를 푼다. 실패 시엔 남겨둬야 다음에 다시 시도한다.
            staleTimeseriesMonths.delete(month);

            return resp.Data;
        } catch (error) {
            const errorData: ErrorStateObj = {
                errorType: 'GLOBAL_ERROR',
                errorMessage: '서버에 일시적인 오류가 발생했습니다. 앱을 다시 실행해주세요.',
            };
            errorStore.setError(errorData);
            console.error('Error fetching timeseries data:', error);
            reportServerAnomaly('timeseries_fetch_failed', toMonthKey(endDate), {
                endDate,
                reportType,
                httpStatus: getErrorStatus(error) ?? null,
                isPublishedMonth: isPublishedMonth(endDate),
            });
            return null; // 에러 시 null 반환
        }
    };

    const getModuleData = async (date: string, reportType = 'Month') => {
        const month = toMonthKey(date);
        const cachedModule = monthlyModuleMap.value[date];

        // 이미 존재하면 재요청하지 않음 (낡음 표시가 붙은 달만 예외)
        if (cachedModule && !staleModuleMonths.has(month)) {
            return cachedModule;
        }
        try {
            // testDate.value가 undefined이면 undefined로 전달
            const nowParam = testDate.value;
            let resp = await getModuleDataApiUser(date, reportType, nowParam);

            // Data.ModuleBodyResDtoList 가 비어 있을 때만 1회 재호출 (유일한 재호출 지점).
            // 주의: module-data 는 GET 이지만 서버에서 ReadCount 를 올린다(비멱등). 이 재호출로
            // 열람수가 1 더 오를 수 있다. 빈 응답일 때만 타므로 빈도는 낮다.
            if (!resp.Data?.ModuleBodyResDtoList) {
                // AIDEV-NOTE: 재호출 전의 열람수를 기억해 둔다.
                // module-data 는 GET 이지만 서버가 ReadCount 를 올려서, 재호출 응답은 1 이 더
                // 큰 값을 들고 온다. 뷰는 ReadCount === 0 으로 "첫 열람"을 판정해
                // 발급 직후 애니메이션을 재생하고 트래킹 카드를 숨기므로, 스토어 내부 사정인
                // 재호출 때문에 그 판정이 어긋나면 안 된다. 첫 응답 기준을 그대로 유지한다.
                // (서버가 빈 응답에서 증가시키지 않았다면 두 값이 같아 이 대입은 무의미하다.)
                const firstReadCount = resp.Data?.ReadCount;

                await delay(EMPTY_PAYLOAD_RETRY_DELAY_MS);
                resp = await getModuleDataApiUser(date, reportType, nowParam);

                if (resp.Data && typeof firstReadCount === 'number') {
                    resp.Data.ReadCount = firstReadCount;
                }

                if (!resp.Data?.ModuleBodyResDtoList) {
                    reportServerAnomaly('module_body_empty', toMonthKey(date), {
                        date,
                        reportType,
                        readCount: resp.Data?.ReadCount ?? null,
                        isPublishedMonth: isPublishedMonth(date),
                    });
                }
            }

            moduleData.value = resp.Data;
            monthlyModuleMap.value[date] = resp.Data;
            staleModuleMonths.delete(month);

            return resp.Data; // ApiResponse 대신 Data만 반환
        } catch (error) {
            console.error('Error fetching module data:', error);
            reportServerAnomaly('module_fetch_failed', toMonthKey(date), {
                date,
                reportType,
                httpStatus: getErrorStatus(error) ?? null,
                isPublishedMonth: isPublishedMonth(date),
            });
            return null; // 에러 시 null 반환
        }
    };

    // const updateCurrentSelectedEvaluation = (evaluation: Evaluation) => {
    //     currentSelectedEvaluation.value = evaluation;
    //     getModuleData(evaluation.EndDate ?? '');
    //     currentModuleEndDate.value = evaluation.EndDate ?? null;
    // };

    const updateUserFeedbackData = (data: UserFeedback) => {
        if (moduleData.value) {
            moduleData.value.UserFeedback = data;
        }
    };

    // const getReportCompletionGage = async () => {
    //     try {
    //         const resp = await getReportCompletionGageApiUser();
    //         if (resp.IsSuccess) {
    //             prepareReportData.value = resp.Data;
    //             return resp.Data;
    //         }
    //     } catch (error) {
    //         console.error('Error fetching report completion gage:', error);
    //     }
    // };

    const getSubscriptionStatus = async (): Promise<boolean> => {
        try {
            const resp = await getSubscriptionStatusApiUser();
            return resp.IsSuccess ? (resp.Data.IsSubscribing ?? false) : false;
        } catch {
            return false;
        }
    };

    const getFeedbackQuestion = async (isPositive: boolean, reportType = 'Month') => {
        try {
            const resp = await getFeedbackQuestionApiUser(currentSelectedEndDate.value ?? '', isPositive, reportType);
            return resp;
        } catch (error) {
            console.error('Error fetching feedback question:', error);
        }
    };

    const postFeedbackAnswer = async (answer: RequestFeedbackData, reportType = 'Month') => {
        try {
            const resp = await postFeedbackAnswerApiUser(currentSelectedEndDate.value ?? '', answer, reportType);
            return resp;
        } catch (error) {
            console.error('Error posting feedback answer:', error);
        }
    };

    const postSubjectiveFeedback = async (subjectiveAnswerValue: string, reportType = 'Month') => {
        try {
            const resp = await postSubjectiveFeedbackApiUser(
                currentSelectedEndDate.value ?? '',
                reportType,
                subjectiveAnswerValue,
            );
            return resp;
        } catch (error) {
            console.error('Error posting subjective feedback:', error);
        }
    };

    // getTimeseriesData 호출 후 getModuleData 호출
    const fetchMonthlyTimeseriesWithModules = async (baseEndDate: string) => {
        const getMonthKey = (dateStr: string) => {
            const [year, month] = dateStr.split('T')[0].split('-');
            return `${year}-${month}`;
        };

        const baseDate = new Date(baseEndDate);
        const monthlyOffsets = [0, -1, -2];
        const newResults: MonthlyTimeseriesData[] = [];

        for (const offset of monthlyOffsets) {
            const targetDate = new Date(baseDate);
            targetDate.setMonth(targetDate.getMonth() + offset);

            const yyyy = targetDate.getFullYear();
            const mm = String(targetDate.getMonth() + 1).padStart(2, '0');
            const dummyEndDate = `${yyyy}-${mm}-01T00:00:00`;
            const monthKey = `${yyyy}-${mm}`;

            // 이미 동일 endDate가 존재하면 호출 생략
            const alreadyExists = monthlyTimeseriesList.value.some(item => getMonthKey(item.endDate) === monthKey);
            if (alreadyExists) continue;

            let timeseries: TimeSeriesResDto | null = null;
            let moduleData: TrendReportResDto | null = null;
            let endDate = dummyEndDate;
            let isError = false;

            try {
                const nowParam = testDate.value;
                const tsResp = await getTimeseriesDataApiUser(endDate, nowParam);
                timeseries = tsResp.Data;
                endDate = tsResp.Data?.Evaluation?.EndDate ?? dummyEndDate;
                monthlyTimeseriesMap.value[endDate] = tsResp.Data;
            } catch {
                isError = true;
            }

            try {
                const nowParam = testDate.value;
                const mdResp = await getModuleDataApiUser(endDate, 'Month', nowParam);
                moduleData = mdResp.Data;
                monthlyModuleMap.value[endDate] = moduleData;
            } catch {
                isError = true;
            }

            newResults.push({
                yearMonthKey: monthKey,
                timeseriesData: timeseries,
                moduleData,
                endDate,
                isError,
            });

            // 기준 월이면 현재 선택된 값 갱신
            if (offset === 0) {
                currentSelectedEndDate.value = endDate;
                timeseriesData.value = timeseries;
            }
        }

        // 병합 및 중복 제거
        const merged = [...monthlyTimeseriesList.value, ...newResults];
        const uniqueSorted = Array.from(new Map(merged.map(item => [item.endDate, item])).values()).sort(
            (a, b) => new Date(a.endDate).getTime() - new Date(b.endDate).getTime(),
        );

        monthlyTimeseriesList.value = uniqueSorted;

        // 현재 화면 표시용 모듈 데이터 지정
        const current = uniqueSorted.find(item => item.endDate === currentSelectedEndDate.value);
        moduleData.value = current?.moduleData ?? null;
    };

    // 해당하는 월만 타겟팅해서 timeseriesData와 moduleData 데이터 호출
    const fetchMonthlyTimeseriesWithModulesForSingleMonth = async (
        endDateStr: string, // 정확한 말일 포맷 (ex: 2023-08-31T00:00:00)
    ) => {
        const getMonthKey = (dateStr: string) => {
            const [year, month] = dateStr.split('T')[0].split('-');
            return `${year}-${new Date(month).getMonth()}`;
        };

        const monthKey = getMonthKey(endDateStr);

        // 이미 존재하면 패스
        // const alreadyExists = monthlyTimeseriesList.value.some(
        //     item => getMonthKey(item.endDate) === monthKey,
        // );
        // if (alreadyExists) return;

        let timeSeriesRespData: TimeSeriesResDto | null = null;
        let moduleRespData: TrendReportResDto | null = null;
        let isError = false;

        try {
            // const tsResp = await getTimeseriesDataApi(
            //     userUid,
            //     endDateStr,
            //     reportType,
            // );
            // timeseriesRespData = tsResp.Data;
            timeSeriesRespData = await getTimeseriesData(endDateStr);
            monthlyTimeseriesMap.value[endDateStr] = timeSeriesRespData;
        } catch {
            isError = true;
        }

        // 타임시리즈 내 평가 데이터가 null 이면 모듈 데이터 호출 생략
        if (timeSeriesRespData?.Evaluation) {
            try {
                moduleRespData = await getModuleData(endDateStr);
                monthlyModuleMap.value[endDateStr] = moduleRespData;
            } catch {
                isError = true;
            }
        } else {
            moduleData.value = null;
        }

        monthlyTimeseriesList.value.push({
            yearMonthKey: monthKey,
            timeseriesData: timeSeriesRespData,
            moduleData: moduleRespData,
            endDate: endDateStr,
            isError,
        });

        // 정렬 및 dedup 처리
        monthlyTimeseriesList.value = Array.from(
            new Map(monthlyTimeseriesList.value.map(item => [item.endDate, item])).values(),
        ).sort((a, b) => new Date(a.endDate).getTime() - new Date(b.endDate).getTime());

        // 현재 선택된 값 갱신
        currentSelectedEndDate.value = endDateStr;
    };

    const parseYMDToDate = (ymd: string): Date => {
        const [dateStr] = ymd.split('T');
        const [year, month, day] = dateStr.split('-').map(Number);
        return new Date(year, month - 1, day); // month는 0부터 시작
    };

    // 현재 선택된 baseEndDate 기준으로 ±3개월 정도만 유지 (캐시 메모리 관리)
    const cleanupMonthlyMapCache = (baseEndDate: string) => {
        const base = parseYMDToDate(baseEndDate);
        const limitRange = 3; // ±3개월 유지

        const keys = Object.keys(monthlyTimeseriesMap.value);

        keys.forEach(key => {
            const date = parseYMDToDate(key);
            const diffMonth = (date.getFullYear() - base.getFullYear()) * 12 + (date.getMonth() - base.getMonth());

            if (Math.abs(diffMonth) >= limitRange) {
                delete monthlyTimeseriesMap.value[key];
                delete monthlyModuleMap.value[key];
            }
        });
    };

    // 중앙 업데이트 함수 (이전, 현재, 이후)
    const updateMonthlySurroundingData = async (baseEndDate: string, reportType = 'Month') => {
        if (!baseEndDate) return;

        const baseDate = parseYMDToDate(baseEndDate);
        const dateOffsets = [-1, 0, 1];

        const targetDates = dateOffsets.map(offset => {
            const target = new Date(baseDate);
            target.setMonth(baseDate.getMonth() + offset);

            const yyyy = target.getFullYear();
            const mm = String(target.getMonth() + 1).padStart(2, '0');
            const dd = '01';
            return `${yyyy}-${mm}-${dd}T00:00:00`;
        });

        if (reportExistingEndDates.value.length === 0) {
            const tsData = await getTimeseriesData(targetDates[1], reportType);
            if (tsData?.Evaluation) {
                await getModuleData(targetDates[1], reportType);
            }
            currentSelectedEndDate.value = baseEndDate;
            return;
        }

        const oldestDate = parseYMDToDate(reportExistingEndDates.value.at(-1)!);
        const latestDate = parseYMDToDate(reportExistingEndDates.value.at(0)!);

        const validTargets = targetDates.filter((dateStr, i) => {
            if (i === 1) return true; // base
            const date = parseYMDToDate(dateStr);
            if (i === 0) {
                const isValid = date >= oldestDate;
                return isValid;
            }
            if (i === 2) {
                const isValid = date <= latestDate;
                return isValid;
            }
            return false;
        });

        const tsResults = await Promise.all(
            validTargets.map(async endDate => {
                const tsData = await getTimeseriesData(endDate, reportType);
                return { endDate, tsData };
            }),
        );

        await Promise.all(
            tsResults.map(async ({ endDate, tsData }) => {
                if (tsData?.Evaluation) {
                    await getModuleData(endDate, reportType);
                }
            }),
        );

        currentSelectedEndDate.value = baseEndDate;
        cleanupMonthlyMapCache(baseEndDate);
    };

    const getEligibilityTrackingCardData = async (now: string | undefined) => {
        // now가 undefined이면 undefined로 전달
        const nowParam = now;
        const resp = await getEligibilityTrackingCardDataApiUser(nowParam);
        eligibilityTrackingCardData.value = resp.Data;
        return resp.Data;
    };

    const postClickReportPublishButton = async (generationDate: string, reportType: string) => {
        const resp = await postClickReportPublishButtonApiUser(generationDate, reportType);
        // 리포트 내용이 아니라 "발급 처리가 됐는지"를 알려주는 boolean 이다.
        const isPublished = resp.Data;

        // 발급이 실제로 된 경우에만 캐시를 버린다.
        // 실패했는데 지우면, 호출부가 `if (res)` 로 막아 재조회가 오지 않으므로
        // 보고 있던 리포트만 화면에서 사라진다.
        if (isPublished) {
            // 진입 시점엔 서버가 이번 달 row 를 조회에서 제외해 Evaluation 없는 응답이 내려오고,
            // 그게 맵에 그대로 저장된다(객체라 truthy 라서 캐시 가드에 걸린다). 발급 버튼의
            // GenerationDateTime 은 getTargetEndDate('LAST_MONTH') 가 만드는 키와 문자열까지
            // 같아서, 클릭 직후 재조회가 정확히 그 낡은 캐시에 적중한다. 그대로 두면
            // `if (tsData?.Evaluation)` 게이트에 막혀 "리포트가 없습니다"가 세션 내내 고착된다.
            //
            // 월 단위로 표시한다. 키 포맷이 조금만 어긋나도(초 단위, dd 값) 조용히 안 걸리고
            // 증상만 남는 사고를 막기 위해서다.
            markMonthStale(generationDate);
            justPublishedMonths.add(toMonthKey(generationDate));
        }

        return resp.Data;
    };

    // testDate 초기화 함수
    const initTestDate = (nowParam?: string) => {
        if (nowParam && /^\d{4}-\d{2}-\d{2}$/.test(nowParam)) {
            // 날짜만 있으면 현재 시간을 추가
            const now = new Date();
            const hours = String(now.getHours()).padStart(2, '0');
            const minutes = String(now.getMinutes()).padStart(2, '0');
            const seconds = String(now.getSeconds()).padStart(2, '0');
            testDate.value = `${nowParam}T${hours}:${minutes}:${seconds}`;
        } else {
            // 아무 설정이 없으면 undefined로 설정
            testDate.value = undefined;
        }
    };

    const getTrendReportShareToken = async (endDate: string, reportType: 'Month' | 'Week' = 'Month') => {
        const resp = await getTrendReportShareTokenApiUser(endDate, reportType);
        return resp.Data;
    };

    const getTrendReportTimeseriesForShare = async (shareToken: string) => {
        const resp = await getTrendReportTimeseriesForShareApiUser(shareToken);
        shareTimeseriesData.value = resp.Data;
        return resp.Data;
    };

    const getTrendReportModuleDataForShare = async (shareToken: string) => {
        const resp = await getTrendReportModuleDataForShareApiUser(shareToken);
        shareModuleData.value = resp.Data;
        return resp.Data;
    };

    return {
        getTimeseriesData,
        getModuleData,
        // updateCurrentSelectedEvaluation,
        // getReportCompletionGage,
        getIsReportVisible,
        getSubscriptionStatus,
        getFeedbackQuestion,
        postFeedbackAnswer,
        postSubjectiveFeedback,
        updateUserFeedbackData,
        fetchMonthlyTimeseriesWithModules,
        fetchMonthlyTimeseriesWithModulesForSingleMonth,
        getReportExistingEndDates,
        updateMonthlySurroundingData,
        getEligibilityTrackingCardData,
        postClickReportPublishButton,
        initTestDate,
        getTrendReportShareToken,
        getTrendReportTimeseriesForShare,
        getTrendReportModuleDataForShare,
        timeseriesData,
        currentSelectedEvaluation,
        prepareReportData,
        moduleDataMap,
        monthlyTimeseriesMap,
        monthlyModuleMap,
        monthlyTimeseriesList,
        currentSelectedEndDate,
        reportExistingEndDates,
        computedTimeseriesData,
        computedModuleData,
        eligibilityTrackingCardData,
        testDate,
        shareTimeseriesData,
        shareModuleData,
        computedShareTimeseriesData,
        computedShareModuleData,
    };
});

export default useProReportStore;
